/**
 * Provider-key API client — Auteur Cloud web app.
 *
 * Campaign `auteur-cloud-commercial-v1`, task `C_VAULT`.
 *
 * This file is the client-side half of threat model §3.9 (lost device). The
 * defeating control is that "the vault API has no endpoint that returns
 * plaintext keys ... the client can only instruct the server to use it or
 * delete it", and the observable is that "the client application makes no
 * network call capable of fetching the plaintext key".
 *
 * That observable is enforced here by construction: the five functions below
 * are the complete set of vault calls the web app can make, they mirror the
 * five routes in `auteur_cloud/api/routes/provider_keys.py` exactly, and none
 * of them has a response shape containing key material.
 * `tests/auteur_cloud/test_vault_operator_blindness.py` parses this file and
 * fails if a sixth endpoint, or any path that looks like a reveal/export/
 * download of a key, appears.
 *
 * Authentication is the `__Host-auteur_session` cookie the platform already
 * sets; `credentials: 'same-origin'` is what sends it. No token is read from
 * or written to `localStorage`/`sessionStorage` here, and neither is a key.
 */

import { requestJson } from '../../api/http';

const BASE = '/v1/provider-keys';

/** Shared fetch wrapper. Never logs a request body — see the module comment. */
async function call(path, { method = 'GET', body } = {}) {
  return requestJson(path, {
    method,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/**
 * List this tenant's provider keys, with the server's readiness projection.
 *
 * Returns masked metadata only: `masked_hint` is a public prefix, an ellipsis
 * and four trailing characters. There is no server field, and therefore no
 * client field, holding a key.
 */
export function listProviderKeys(workspaceId) {
  // With a workspace id, the response's `readiness` reports that workspace's
  // approvals only (never another workspace's); without one it reports none.
  return call(workspaceId ? `${BASE}?workspace_id=${encodeURIComponent(workspaceId)}` : BASE);
}

/**
 * Store a key. Write-only: the value submitted here can never be read back,
 * by this client or by any operator.
 */
export function storeProviderKey({ provider, label, secret }) {
  return call(BASE, { method: 'POST', body: { provider, label, secret } });
}

/** Replace the material behind an existing key. Same write-only property. */
export function replaceProviderKey(keyId, { secret }) {
  return call(`${BASE}/${encodeURIComponent(keyId)}`, { method: 'PUT', body: { secret } });
}

/**
 * Destroy a key. Resolves with the server's destruction proof (a revocation
 * id and timestamp), which the UI shows to the customer rather than a bare
 * "deleted" toast.
 */
export function deleteProviderKey(keyId) {
  return call(`${BASE}/${encodeURIComponent(keyId)}`, { method: 'DELETE' });
}

/**
 * Ask the server to check a stored key against its provider.
 *
 * Explicitly customer-triggered: nothing in this client calls it on mount, on
 * a timer, or as a side effect of storing a key. The response is a status
 * string, never a provider response body.
 */
export function validateProviderKey(keyId) {
  return call(`${BASE}/${encodeURIComponent(keyId)}/validate`, { method: 'POST', body: {} });
}

/**
 * The complete set of vault operations available to this client.
 *
 * Exported as data so the operator-blindness suite can assert the set rather
 * than grep for absences, and so a reviewer can see at a glance that "read a
 * key" is not among them.
 */
export const PROVIDER_KEY_OPERATIONS = Object.freeze([
  'list',
  'store',
  'replace',
  'delete',
  'validate',
]);
